import { useCallback, useReducer, useRef } from 'react'
import env from '@/config/env'
import { getApiHeaders } from '@/config/trpc'
import type { AnalyzeStreamEvent } from '@backend/lib/ai/prompts/moodboard/stream-events'
import type { MoodboardBrief, MoodboardDepartment } from '@backend/lib/db/schema/moodboard'

// Hook that drives the moodboard analyze stream. Opens an NDJSON POST to
// /stream/moodboard/analyze, parses incrementally, and exposes a phase-aware
// state machine the sidebar UI can render against.

export type DeptStatus = 'pending' | 'streaming' | 'done' | 'cached'

export type DeptState = {
  status: DeptStatus
  memo: string
}

export type AnalyzeStreamState = {
  phase: 'idle' | 'extract' | 'depts' | 'summarize' | 'done' | 'error'
  extract: {
    steps: string[]
    completed: string[]
  }
  depts: Record<MoodboardDepartment, DeptState>
  partialBrief: MoodboardBrief | null
  finalBrief: MoodboardBrief | null
  cached: boolean
  error: string | null
}

const EMPTY_DEPT: DeptState = { status: 'pending', memo: '' }

const INITIAL_STATE: AnalyzeStreamState = {
  phase: 'idle',
  extract: { steps: [], completed: [] },
  depts: { strategy: EMPTY_DEPT, voice: EMPTY_DEPT, design: EMPTY_DEPT },
  partialBrief: null,
  finalBrief: null,
  cached: false,
  error: null,
}

type Action = { type: 'reset' } | { type: 'event'; event: AnalyzeStreamEvent }

function reducer(state: AnalyzeStreamState, action: Action): AnalyzeStreamState {
  if (action.type === 'reset') return { ...INITIAL_STATE, phase: 'extract' }
  const ev = action.event
  switch (ev.type) {
    case 'extract_start':
      return {
        ...state,
        phase: 'extract',
        extract: { steps: ev.steps, completed: [] },
      }
    case 'extract_step_done':
      return {
        ...state,
        extract: { ...state.extract, completed: [...state.extract.completed, ev.label] },
      }
    case 'extract_done':
      return { ...state, phase: 'depts' }
    case 'dept_start':
      return {
        ...state,
        phase: 'depts',
        depts: { ...state.depts, [ev.dept]: { status: 'streaming', memo: '' } },
      }
    case 'dept_delta':
      return {
        ...state,
        depts: {
          ...state.depts,
          [ev.dept]: {
            status: 'streaming',
            memo: state.depts[ev.dept].memo + ev.delta,
          },
        },
      }
    case 'dept_done':
      return {
        ...state,
        depts: {
          ...state.depts,
          [ev.dept]: { status: ev.cached ? 'cached' : 'done', memo: ev.memo },
        },
      }
    case 'summarize_start':
      return { ...state, phase: 'summarize' }
    case 'summarize_partial':
      // Lenient cast — partial-object stream is allowed to have undefined
      // / missing fields; the UI is defensive about it.
      return { ...state, partialBrief: ev.brief as MoodboardBrief }
    case 'summarize_done':
      return { ...state, partialBrief: ev.brief, finalBrief: ev.brief }
    case 'done':
      return {
        ...state,
        phase: 'done',
        finalBrief: ev.brief,
        partialBrief: ev.brief,
        cached: ev.cached,
      }
    case 'error':
      return { ...state, phase: 'error', error: ev.message }
    default:
      return state
  }
}

export function useAnalyzeStream(personaId: number) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE)
  const abortRef = useRef<AbortController | null>(null)

  const start = useCallback(
    async (force = true, enabledAnalyzers?: MoodboardDepartment[]) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      dispatch({ type: 'reset' })

      let response: Response
      try {
        response = await fetch(`${env.VITE_BACKEND_URL}/stream/moodboard/analyze`, {
          method: 'POST',
          headers: { ...getApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: personaId, force, enabledAnalyzers }),
          signal: controller.signal,
        })
      } catch (err) {
        if (controller.signal.aborted) return
        dispatch({ type: 'event', event: { type: 'error', message: String(err) } })
        return
      }

      if (!response.ok || !response.body) {
        dispatch({
          type: 'event',
          event: { type: 'error', message: `Analyze failed (${response.status})` },
        })
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          let newlineIdx = buffer.indexOf('\n')
          while (newlineIdx !== -1) {
            const line = buffer.slice(0, newlineIdx).trim()
            buffer = buffer.slice(newlineIdx + 1)
            if (line) {
              try {
                const parsed = JSON.parse(line) as AnalyzeStreamEvent
                dispatch({ type: 'event', event: parsed })
              } catch {
                // Malformed line — skip and keep streaming.
              }
            }
            newlineIdx = buffer.indexOf('\n')
          }
        }
        // Drain any trailing line without a final newline.
        const tail = buffer.trim()
        if (tail) {
          try {
            const parsed = JSON.parse(tail) as AnalyzeStreamEvent
            dispatch({ type: 'event', event: parsed })
          } catch {
            // Ignore — server should always terminate with newline.
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          dispatch({ type: 'event', event: { type: 'error', message: String(err) } })
        }
      } finally {
        reader.releaseLock()
      }
    },
    [personaId],
  )

  const abort = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  return { state, start, abort }
}
